/**
 * UnifiedLogin - iOS 26 with Dual Auth (Magic Link + PIN)
 * Paliacate OS — Marzo 2026
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Loader2, ArrowRight, Shield, Users, Briefcase, ArrowLeft, CheckCircle, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PinInput from '../components/auth/PinInput';
import { initSessionManager } from '../services/sessionService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnifiedLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [detectedRole, setDetectedRole] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [cooldown, setCooldown] = useState(0);
  const [mode, setMode] = useState('pin'); // 'pin' | 'magic'

  useEffect(() => {
    const checkExistingSession = async () => {
      const token = localStorage.getItem('portal_token');
      if (!token) { setCheckingSession(false); return; }
      try {
        const response = await axios.get(`${API_URL}/api/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.valid) {
          const role = response.data.role;
          if (role === 'admin') navigate('/admin', { replace: true });
          else if (role === 'worker') navigate('/worker/dashboard', { replace: true });
          else if (role === 'client') navigate('/portal/dashboard', { replace: true });
          else setCheckingSession(false);
        } else {
          localStorage.removeItem('portal_token');
          localStorage.removeItem('portal_user');
          setCheckingSession(false);
        }
      } catch {
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_user');
        setCheckingSession(false);
      }
    };
    checkExistingSession();
  }, [navigate]);

  const handlePinLogin = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Ingresa tu correo electronico'); return; }
    if (pin.length !== 6) { toast.error('Ingresa tu PIN de 6 digitos'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login-pin`, { email: email.trim(), pin });
      const { token, role, system_role, user_id, name, redirect_path } = response.data;

      // Clear old tokens
      localStorage.removeItem('portal_token');
      localStorage.removeItem('portal_user');
      localStorage.removeItem('worker_token');
      localStorage.removeItem('worker_user');

      // Store new session — JWT lives in httpOnly cookie; we only persist
      // non-sensitive user info here for UI rendering.
      const userData = { email: response.data.email, role, system_role, user_id, id: user_id, name,
        worker_id: role === 'worker' ? user_id : null,
        client_id: role === 'client' ? user_id : null,
      };
      localStorage.setItem('portal_user', JSON.stringify(userData));
      localStorage.setItem('user', JSON.stringify(userData));
      if (role === 'worker') {
        localStorage.setItem('worker_user', JSON.stringify(userData));
      }

      initSessionManager();
      toast.success('Bienvenido de vuelta');
      navigate(redirect_path || '/admin', { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 429) {
        toast.error(detail);
        setMode('magic');
      } else {
        toast.error(detail || 'Credenciales incorrectas');
      }
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Ingresa tu correo electronico'); return; }
    if (cooldown > 0) { toast.info(`Espera ${cooldown} segundos`); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/unified-auth/login`, { email: email.trim() });
      setDetectedRole(response.data.role);
      setMagicLinkSent(true);
      toast.success('Enlace enviado a tu correo');
      setCooldown(60);
      const interval = setInterval(() => {
        setCooldown(prev => { if (prev <= 1) { clearInterval(interval); return 0; } return prev - 1; });
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el enlace');
    } finally {
      setLoading(false);
    }
  };

  const roleInfo = {
    admin: { icon: Shield, title: 'Administrador', bgColor: 'var(--ios-red-bg)', textColor: 'var(--ios-red-text)' },
    worker: { icon: Users, title: 'Colaborador', bgColor: 'var(--ios-blue-bg)', textColor: 'var(--ios-blue-text)' },
    client: { icon: Briefcase, title: 'Cliente', bgColor: 'var(--ios-green-bg)', textColor: 'var(--ios-green-text)' }
  };

  if (checkingSession) {
    return (
      <div className="ios-page flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: 'var(--ios-accent)' }} />
          <p style={{ color: 'var(--ios-text-secondary)' }}>Verificando sesion...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ios-page flex flex-col" data-testid="login-page">
      <header className="p-6 relative z-10">
        <Link to="/" className="ios-back-btn inline-flex">
          <ArrowLeft className="w-4 h-4" />
          <span>Volver al inicio</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="ios-sidebar-logo-icon w-16 h-16 mx-auto mb-4" style={{ fontSize: '28px', borderRadius: '18px' }}>P</div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--ios-text-primary)', letterSpacing: '-0.3px' }}>
              Paliacate OS
            </h1>
          </div>

          {/* Login Card */}
          <div className="ios-card p-8" data-testid="login-card">
            {!magicLinkSent ? (
              <>
                <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--ios-text-primary)', marginBottom: '8px' }}>
                  Iniciar sesion
                </h2>
                <p style={{ color: 'var(--ios-text-secondary)', fontSize: '14px', marginBottom: '24px' }}>
                  {mode === 'pin' ? 'Ingresa tu correo y PIN para acceder' : 'Te enviaremos un enlace de acceso a tu correo'}
                </p>

                <form onSubmit={mode === 'pin' ? handlePinLogin : handleMagicLink} className="space-y-4">
                  {/* Email field */}
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--ios-text-secondary)', marginBottom: '8px' }}>
                      Correo electronico
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--ios-text-tertiary)' }} />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="tu@correo.com"
                        className="ios-input pl-10"
                        disabled={loading}
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  {/* PIN field - only in PIN mode */}
                  {mode === 'pin' && (
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--ios-text-secondary)', marginBottom: '10px' }}>
                        PIN de acceso
                      </label>
                      <PinInput value={pin} onChange={setPin} disabled={loading} />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="ios-btn-primary w-full py-3"
                    data-testid="login-button"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> {mode === 'pin' ? 'Entrando...' : 'Enviando...'}</>
                    ) : mode === 'pin' ? (
                      <>Entrar <ArrowRight className="w-5 h-5" /></>
                    ) : (
                      <>Enviar Magic Link <Mail className="w-5 h-5" /></>
                    )}
                  </button>
                </form>

                {/* Toggle between PIN and Magic Link */}
                <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  {mode === 'pin' ? (
                    <button
                      onClick={() => setMode('magic')}
                      className="w-full text-center"
                      style={{ color: 'var(--ios-text-secondary)', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer' }}
                      data-testid="switch-to-magic-link"
                    >
                      ¿No tienes PIN o lo olvidaste?{' '}
                      <span style={{ color: 'var(--ios-accent, #e03c3c)', fontWeight: 500 }}>Enviar Magic Link</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setMode('pin')}
                      className="w-full text-center"
                      style={{ color: 'var(--ios-text-secondary)', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer' }}
                      data-testid="switch-to-pin"
                    >
                      ¿Ya tienes PIN?{' '}
                      <span style={{ color: 'var(--ios-accent, #e03c3c)', fontWeight: 500 }}>Entrar con PIN</span>
                    </button>
                  )}
                </div>

                {/* Role indicators */}
                <div className="mt-6 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  <p style={{ color: 'var(--ios-text-tertiary)', fontSize: '11px', textAlign: 'center', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
                    Acceso para
                  </p>
                  <div className="flex justify-center gap-6">
                    {Object.entries(roleInfo).map(([key, info]) => (
                      <div key={key} className="flex flex-col items-center gap-2">
                        <div className="p-2.5 rounded-xl" style={{ background: info.bgColor }}>
                          <info.icon className="w-5 h-5" style={{ color: info.textColor }} />
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--ios-text-tertiary)' }}>{info.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              /* Magic Link Success State */
              <div className="text-center py-4" data-testid="login-success">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--ios-green-bg)' }}>
                  <CheckCircle className="w-8 h-8" style={{ color: 'var(--ios-green)' }} />
                </div>
                <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--ios-text-primary)', marginBottom: '8px' }}>
                  Revisa tu correo
                </h2>
                <p style={{ color: 'var(--ios-text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
                  Enviamos un enlace de acceso a <span style={{ fontWeight: 500, color: 'var(--ios-text-primary)' }}>{email}</span>
                </p>
                
                {detectedRole && roleInfo[detectedRole] && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: roleInfo[detectedRole].bgColor }}>
                    {React.createElement(roleInfo[detectedRole].icon, { className: "w-4 h-4", style: { color: roleInfo[detectedRole].textColor } })}
                    <span style={{ fontSize: '14px', fontWeight: 500, color: roleInfo[detectedRole].textColor }}>{roleInfo[detectedRole].title}</span>
                  </div>
                )}

                <p style={{ color: 'var(--ios-text-tertiary)', fontSize: '12px', marginTop: '24px' }}>
                  {cooldown > 0 
                    ? `Puedes solicitar otro enlace en ${cooldown}s`
                    : 'El enlace es valido por 7 dias'
                  }
                </p>

                {cooldown === 0 && (
                  <button
                    onClick={() => setMagicLinkSent(false)}
                    className="ios-btn-ghost mt-4"
                  >
                    Enviar nuevo enlace
                  </button>
                )}
              </div>
            )}
          </div>

          <p style={{ textAlign: 'center', color: 'var(--ios-text-tertiary)', fontSize: '12px', marginTop: '32px' }}>
            &copy; 2026 Paliacate Studio
          </p>
        </div>
      </main>
    </div>
  );
}
